import { SharedService } from './../../../services/shared.service';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NotificationsService } from '@services/notifications.service';
import { PagesService } from '@services/pages.service';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent {
  notif: any[] = [];
  currentPage = 1;
  pageSize = 6;
  totalPages = 0;
  activeTab: 'all' | 'read' | 'unread' = 'all'; // ✅ NEW: Track active tab

  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  notificationsService = inject(NotificationsService);


  ngOnInit(): void {
    this.getNotificationList();

    // ✅ Subscribe to service notif array for real-time updates
    this.subscribeToRealTimeNotifications();
  }

  subscribeToRealTimeNotifications() {
    // Update local list when service list changes
    setInterval(() => {
      if (this.notificationsService.notif.length !== this.notif.length) {
        this.notif = [...this.notificationsService.notif];
        this.updateTotalPages(); // ✅ UPDATED: Use new method
      }
    }, 1000); // Check every second
  }

  // ✅ NEW: Method to update total pages based on filtered results
  updateTotalPages() {
    this.totalPages = Math.ceil(this.filteredNotifications.length / this.pageSize);
  }

  async getNotificationList() {
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_NOTIFICATION').subscribe({
      next: (response: any) => {
        // ✅ Also update service notif array
        this.notificationsService.notif = response;
        this.notif = response;
        this.updateTotalPages(); // ✅ UPDATED: Use new method
        this.sharedService.hideLoader();
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(error, error);
      },
    });
  }

  async markAllAsRead() {
    // Get all unread notification IDs
    const unreadNotifications = this.notif.filter(
      (item) => !item._notification_logs_of_notifications?.is_read
    );

    if (unreadNotifications.length === 0) {
      return;
    }

    const notificationIds = unreadNotifications.map((item) => item.id);

    // Single bulk API call
    const success = await this.notificationsService.markAllNotificationsRead(notificationIds);

    if (success) {
      // Update UI for all unread notifications
      unreadNotifications.forEach((item) => {
        if (!item._notification_logs_of_notifications) {
          item._notification_logs_of_notifications = {
            id: 0,
            notification_id: item.id,
            is_read: true,
            is_delivered: true,
            read_at: Date.now(),
            delivered_at: Date.now(),
          };
        } else {
          item._notification_logs_of_notifications.is_read = true;
          item._notification_logs_of_notifications.read_at = Date.now();
        }
      });

      // Reset counter
      this.notificationsService.totalNewNotifications.next(0);
    }
  }

  markAsRead(item: any) {
    // If _notification_logs_of_notifications doesn't exist, create it
    if (!item._notification_logs_of_notifications) {
      const payload = {
        id: 0,
        notification_id: item.id,
        is_read: true,
        is_delivered: true,
        read_at: Date.now(),
        delivered_at: Date.now(),
      };

      // Create the object for UI update
      item._notification_logs_of_notifications = { ...payload };

      // 🔹 Counter update turant (only if it was unread)
      this.notificationsService.totalNewNotifications.next(
        Math.max(0, this.notificationsService.totalNewNotifications.value - 1)
      );

      this.notificationsService.updateNotification(payload);
      return;
    }

    // If already read, don't do anything
    if (item._notification_logs_of_notifications.is_read) {
      return;
    }

    // Mark as read
    const payload = {
      ...item._notification_logs_of_notifications,
      is_read: true,
      read_at: Date.now(),
    };

    item._notification_logs_of_notifications.is_read = true;

    // 🔹 Counter update turant
    this.notificationsService.totalNewNotifications.next(
      Math.max(0, this.notificationsService.totalNewNotifications.value - 1)
    );

    this.notificationsService.updateNotification(payload);
  }

  markAsUnread(item: any) {

    if (item._notification_logs_of_notifications?.is_read) {
      const payload = {
        ...item._notification_logs_of_notifications,
        is_read: false,
        read_at: null,
      };
      item._notification_logs_of_notifications.is_read = false;

      this.notificationsService.totalNewNotifications.next(
        this.notificationsService.totalNewNotifications.value + 1
      );
      this.notificationsService.updateNotification(payload);
    }
  }

  getTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    const months = Math.floor(days / 30);
    const remainingWeeks = Math.floor((days % 30) / 7);
    const years = Math.floor(days / 365);
    const remainingMonths = Math.floor((days % 365) / 30);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

    // 👇 handle days and weeks together
    if (days < 30) {
      if (weeks >= 1 && remainingDays > 0) {
        return `${weeks} week${weeks > 1 ? 's' : ''} and ${remainingDays} day${remainingDays > 1 ? 's' : ''} ago`;
      }
      if (weeks >= 1) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }

    // 👇 handle months and weeks
    if (days < 365) {
      if (months >= 1 && remainingWeeks > 0) {
        return `${months} month${months > 1 ? 's' : ''} and ${remainingWeeks} week${remainingWeeks > 1 ? 's' : ''} ago`;
      }
      return `${months} month${months > 1 ? 's' : ''} ago`;
    }

    // 👇 handle years and months
    if (years >= 1 && remainingMonths > 0) {
      return `${years} year${years > 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''} ago`;
    }

    return `${years} year${years > 1 ? 's' : ''} ago`;
  }

  // ✅ NEW: Get filtered notifications based on active tab
  get filteredNotifications() {
    switch (this.activeTab) {
      case 'read':
        return this.notif.filter(item => item._notification_logs_of_notifications?.is_read);
      case 'unread':
        return this.notif.filter(item => !item._notification_logs_of_notifications?.is_read);
      default:
        return this.notif;
    }
  }

  // ✅ UPDATED: Get notifications for current page from filtered results
  get pagedNotifications() {
    const filtered = this.filteredNotifications;
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return filtered.slice(start, end);
  }

  // ✅ NEW: Method to change active tab
  setActiveTab(tab: 'all' | 'read' | 'unread') {
    this.activeTab = tab;
    this.currentPage = 1; // Reset to first page when changing tabs
    this.updateTotalPages();
  }

  goToPage(page: any) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  prevPage() {
    if (this.currentPage > 1) this.currentPage--;
  }

  // 🔹 NEW METHOD: Smart pagination display
  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // Maximum visible page numbers

    if (this.totalPages <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (this.currentPage <= 3) {
        // Near start: 1 2 3 4 ... last
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        // Near end: 1 ... last-3 last-2 last-1 last
        pages.push('...');
        for (let i = this.totalPages - 3; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle: 1 ... current-1 current current+1 ... last
        pages.push('...');
        pages.push(this.currentPage - 1);
        pages.push(this.currentPage);
        pages.push(this.currentPage + 1);
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    return pages;
  }
}